import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { openInquiry } from "@/lib/scroll";
import CAMPAIGN from "@/data/campaign";

const EASE = [0.76, 0, 0.24, 1];

const FAQS = [
  {
    q: "Do Indians need a visa for Bhutan?",
    a: "No visa needed. Indians get an entry permit at Phuentsholing with an original passport or voter ID — our team handles the entire permit process for you on Day 1.",
  },
  {
    q: "Can I join with flights from my city?",
    a: "Yes. Join the group at Bagdogra for ₹30,900 + taxes, or fly from Mumbai on our flight-inclusive departures — 27 Sep at ₹55,000, 20 Dec and 27 Dec at ₹60,000. From any other city, our team builds a flight-inclusive quote on the callback.",
  },
  {
    q: "What kind of hotels are included?",
    a: "Comfortable 3-star hotels on twin sharing across Phuentsholing, Thimphu, Punakha and Paro — clean rooms, warm hosts, and locations that keep drives short.",
  },
  {
    q: "I'm travelling solo. How will I be accommodated?",
    a: "Most of our batches have solo travellers. You share a twin room with a same-gender traveller from the group, or opt for a private room at a supplement — just tell our team on the call.",
  },
  {
    q: "Are SDF and permit charges included in ₹30,900?",
    a: "Yes — visa, entry permit and Sustainable Development Fee (SDF) charges are all covered in the package price. No surprise add-ons when you land.",
  },
  {
    q: "What currency works in Bhutan?",
    a: "The Bhutanese Ngultrum is pegged 1:1 to the Indian Rupee, and Indian currency is widely accepted everywhere. Carry some cash for markets and cafes.",
  },
  {
    q: "Is vegetarian food available?",
    a: "Yes — every meal. Bhutanese kitchens do excellent veg dishes, and your trip captain makes sure veg and Jain preferences are sorted at every stop.",
  },
  {
    q: "What ID proofs should I carry?",
    a: "An original passport or voter ID is mandatory for the Bhutan entry permit. Carry 2-3 passport-size photos and photocopies as backup — we guide you through everything.",
  },
  {
    q: "Is Bhutan safe for solo female travellers?",
    a: "Bhutan is consistently ranked among the safest countries in the world, and our trip captain travels with the group from Bagdogra to Bagdogra with 24/7 support.",
  },
];

const FAQ = () => (
  <section id="faq" className="py-24 md:py-36 border-t border-border" data-testid="faq-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
      <div className="lg:col-span-5">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="uppercase text-xs tracking-[0.25em] font-semibold text-primary mb-6"
          data-testid="faq-overline"
        >
          Before you ask
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-heading font-light tracking-tight text-foreground text-4xl sm:text-5xl leading-[1.05]"
          data-testid="faq-heading"
        >
          Everything people<br />ask us <em className="text-primary">first.</em>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          className="mt-8 text-base md:text-lg font-light text-foreground/75 leading-relaxed max-w-md"
        >
          Something more specific?{" "}
          <button onClick={openInquiry} className="text-primary border-b border-primary/40 hover:border-primary transition-colors duration-300" data-testid="faq-cta-button">
            Ask us directly
          </button>{" "}
          — a real human replies within 24 hours.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
        className="lg:col-span-7"
      >
        <Accordion type="single" collapsible className="border-t border-border">
          {CAMPAIGN.faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-heading text-xl md:text-2xl font-medium text-foreground hover:text-primary hover:no-underline py-6 transition-colors duration-300" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base font-light text-foreground/75 leading-relaxed pb-6 max-w-2xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </div>
  </section>
);

export default FAQ;
